import React, { useState } from 'react';
import { sourceCrsChoices } from '../lib/coordinates';
import { resolveUtmFromTable } from '../lib/auto-detect';
import { buildXYZGeoJSON } from '../lib/xyz-geojson';
import { useTheme } from '../contexts/ThemeContext';

const COORD_FORMATS = [
  { id: 'dd', label: 'Decimal Degrees' },
  { id: 'dms', label: 'DMS (deg° min\' sec")' },
  { id: 'utm', label: 'UTM / Projected' },
];

const EXTRA_FIELDS = [
  { id: 'label', label: 'Label' },
  { id: 'lineId', label: 'Line ID' },
  { id: 'date', label: 'Date' },
  { id: 'qc', label: 'QC Status' },
];

/** localStorage key for remembered mappings, keyed by the header signature. */
export const mappingStorageKey = (headers) => `xyz-mapping:${headers.join('|').toLowerCase()}`;

/**
 * ColumnMappingDialog — map tabular columns to X/Y/Z roles (XYZ pipeline)
 *
 * Props:
 *   fileName  - display name
 *   headers   - string[] column headers
 *   rows      - string[][] data rows (preview uses the first 20)
 *   detection - result of detectColumns() — pre-fills the dropdowns
 *   initialError - message shown when the dialog opens (e.g. a saved mapping that no longer plots)
 *   onApply(mapping, geojson) - mapping { x, y, z:[...], activeZ, crs, format, extras, headers };
 *     geojson is the layer built from it. Apply & Plot builds it here first, so every problem
 *     (no data rows, no zone, out-of-range lat/lon, no valid rows) stays in the dialog.
 *   onClose()
 */
export default function ColumnMappingDialog({ fileName, headers = [], rows = [], detection = {}, hint = '', initialError = null, onApply, onClose }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const [x, setX] = useState(detection.x ?? -1);
  const [y, setY] = useState(detection.y ?? -1);
  const [zCols, setZCols] = useState(detection.z?.slice(0, 4) || []);
  const [crs, setCrs] = useState(detection.crs || '');
  const [crsQuery, setCrsQuery] = useState('');
  const [format, setFormat] = useState(
    detection.projected || (detection.crs && detection.crs !== 'EPSG:4326') ? 'utm' : 'dd'
  );
  const [extras, setExtras] = useState({});
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState(initialError || (rows.length === 0 ? 'The file has no data rows' : null));

  const preview = rows.slice(0, 20);
  const confident = (score) => (score ?? 0) >= 0.6;

  const toggleZ = (i) => {
    setZCols(prev => prev.includes(i) ? prev.filter(c => c !== i) : [...prev, i]);
  };

  const colRole = (i) => {
    if (i === x) return { tag: 'X', color: C.cyan };
    if (i === y) return { tag: 'Y', color: C.emerald };
    if (zCols.includes(i)) return { tag: 'Z', color: C.amber };
    return null;
  };

  const crsChoices = sourceCrsChoices(crsQuery);
  const locationHint = `${fileName || ''} ${hint || ''}`;
  const autoZone = crs === 'UTM_AUTO' ? resolveUtmFromTable(headers, rows, x, y, locationHint) : null;

  const apply = () => {
    if (rows.length === 0) { setError('The file has no data rows'); return; }
    if (x < 0 || y < 0) { setError('Select both X and Y columns'); return; }
    if (x === y) { setError('X and Y must be different columns'); return; }
    if (zCols.length === 0) { setError('Select at least one Z (value) column'); return; }
    if (!crs) {
      setError('Choose a coordinate system. Projected easting and northing are not placed until you pick a UTM zone or EPSG code.');
      return;
    }
    let chosen = crs;
    if (chosen === 'UTM_AUTO') {
      if (!autoZone) {
        setError('Auto zone could not find a location in this file. Put the zone in a header (UTM 16N or EPSG:32616), include longitude and latitude, or pick the zone.');
        return;
      }
      chosen = autoZone.code;
    }
    const mapping = { x, y, z: zCols, activeZ: zCols[0], crs: chosen, format, extras, headers, fileName, hint };
    let geojson;
    try {
      geojson = buildXYZGeoJSON(headers, rows, mapping);
    } catch (e) {
      setError(e.message || 'Could not place these coordinates');
      return;
    }
    if (!geojson.features?.length) {
      const skipped = geojson.metadata?.xyz?.skippedRows || 0;
      setError(skipped
        ? `None of the ${rows.length} rows landed on the map in ${chosen}. ${skipped} were out of range — check the zone.`
        : `No rows could be placed in ${chosen}.`);
      return;
    }
    if (remember) {
      try { localStorage.setItem(mappingStorageKey(headers), JSON.stringify(mapping)); } catch { /* quota */ }
    }
    onApply(mapping, geojson);
  };

  const selStyle = { width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' };
  const lbl = { fontSize: 10, color: C.text2, display: 'block', marginBottom: 2 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 860, maxWidth: '95vw', maxHeight: '90vh', background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.text0 }}>🧭 Column Mapping</span>
          <span style={{ fontSize: 11, color: C.text3 }}>{fileName} · {rows.length} rows · {headers.length} columns</span>
          {detection.confidence !== undefined && (
            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, marginLeft: 6,
              background: detection.confidence >= 0.8 ? 'rgba(52,211,153,0.12)' : 'rgba(251,191,36,0.12)',
              color: detection.confidence >= 0.8 ? C.emerald : C.amber,
              border: `1px solid ${detection.confidence >= 0.8 ? 'rgba(52,211,153,0.3)' : 'rgba(251,191,36,0.3)'}` }}>
              auto-detect {(detection.confidence * 100).toFixed(0)}%
            </span>
          )}
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Controls */}
          <div style={{ width: 250, padding: 14, borderRight: `1px solid ${C.border}`, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label style={lbl}>
                X (Easting / Longitude) {confident(detection.xScore) ? <span style={{ color: C.emerald }}>✓</span> : <span style={{ color: C.amber }}>?</span>}
              </label>
              <select value={x} onChange={e => setX(parseInt(e.target.value))} style={{ ...selStyle, borderColor: x >= 0 ? C.accentBorder : C.border }}>
                <option value={-1}>Select column...</option>
                {headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>
                Y (Northing / Latitude) {confident(detection.yScore) ? <span style={{ color: C.emerald }}>✓</span> : <span style={{ color: C.amber }}>?</span>}
              </label>
              <select value={y} onChange={e => setY(parseInt(e.target.value))} style={{ ...selStyle, borderColor: y >= 0 ? 'rgba(52,211,153,0.4)' : C.border }}>
                <option value={-1}>Select column...</option>
                {headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
              </select>
            </div>

            <div>
              <label style={lbl}>Z Value Columns (multiple allowed)</label>
              <div style={{ maxHeight: 120, overflow: 'auto', background: C.bg3, borderRadius: 6, border: `1px solid ${C.border}`, padding: 6 }}>
                {headers.map((h, i) => (i === x || i === y) ? null : (
                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: zCols.includes(i) ? C.amber : C.text2, cursor: 'pointer', padding: '2px 4px' }}>
                    <input type="checkbox" checked={zCols.includes(i)} onChange={() => toggleZ(i)} style={{ accentColor: C.amber }} />
                    {h}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label style={lbl}>Coordinate System</label>
              <input
                value={crsQuery}
                onChange={e => setCrsQuery(e.target.value)}
                placeholder="Filter: 16N, UTM 10S, EPSG:32610"
                aria-label="Filter coordinate systems"
                style={{ ...selStyle, marginBottom: 4 }}
              />
              <select value={crs} onChange={e => setCrs(e.target.value)} style={selStyle} aria-label="Coordinate system">
                <option value="">Choose coordinate system…</option>
                {crs && !crsChoices.some(c => c.code === crs) && <option value={crs}>{crs}</option>}
                {crsChoices.map(c => (
                  <option key={c.code} value={c.code}>{c.label === c.code ? c.code : `${c.label} (${c.code})`}</option>
                ))}
              </select>
              {crs === 'UTM_AUTO' && autoZone && (
                <div style={{ fontSize: 10, color: C.emerald, marginTop: 3 }}>
                  Auto zone {autoZone.zone}{autoZone.north ? 'N' : 'S'} ({autoZone.code}) from this file&apos;s location
                </div>
              )}
              {!crs && detection.projected && (
                <div style={{ fontSize: 10, color: C.amber, marginTop: 3 }}>
                  No zone was assumed. Pick UTM 10N, UTM 16N, or another EPSG code.
                </div>
              )}
            </div>

            <div>
              <label style={lbl}>Coordinate Format</label>
              <select value={format} onChange={e => setFormat(e.target.value)} style={selStyle}>
                {COORD_FORMATS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </div>

            <div>
              <label style={lbl}>Additional Fields (optional)</label>
              {EXTRA_FIELDS.map(f => (
                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 10, color: C.text2, width: 52 }}>{f.label}</span>
                  <select value={extras[f.id] ?? -1} onChange={e => setExtras(p => ({ ...p, [f.id]: parseInt(e.target.value) }))}
                    style={{ ...selStyle, flex: 1 }}>
                    <option value={-1}>—</option>
                    {headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>

            {detection.notes?.map((n, i) => (
              <div key={i} style={{ fontSize: 10, color: C.amber, padding: '4px 8px', background: 'rgba(251,191,36,0.08)', borderRadius: 6 }}>⚠ {n}</div>
            ))}

            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.text2, cursor: 'pointer' }}>
              <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} style={{ accentColor: C.cyan }} />
              Remember this format
            </label>
          </div>

          {/* Preview table */}
          <div style={{ flex: 1, overflow: 'auto', padding: 10 }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 10, width: '100%' }}>
              <thead>
                <tr>
                  {headers.map((h, i) => {
                    const role = colRole(i);
                    return (
                      <th key={i} style={{
                        padding: '5px 8px', textAlign: 'left', position: 'sticky', top: 0,
                        background: role ? `${role.color}22` : C.bg1,
                        color: role ? role.color : C.text1,
                        borderBottom: `2px solid ${role ? role.color : C.border}`,
                        whiteSpace: 'nowrap',
                      }}>
                        {role && <span style={{ fontWeight: 700, marginRight: 4 }}>[{role.tag}]</span>}
                        {h}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, ri) => (
                  <tr key={ri}>
                    {headers.map((_, ci) => {
                      const role = colRole(ci);
                      return (
                        <td key={ci} style={{
                          padding: '3px 8px', color: role ? role.color : C.text2,
                          background: role ? `${role.color}0d` : 'transparent',
                          borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
                          fontFamily: 'monospace',
                        }}>
                          {row[ci] ?? ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          {error && <span role="alert" style={{ fontSize: 11, color: C.red, flex: '1 1 auto', maxHeight: 64, overflow: 'auto' }}>✗ {error}</span>}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg4, color: C.text1, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
          <button onClick={apply}
            style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: 'linear-gradient(135deg,#00b8de,#3b82f6)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>
            Apply & Plot
          </button>
        </div>
      </div>
    </div>
  );
}
